import React from 'react';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Divider,
  Button,
  Paper,
  ListItemButton
} from '@mui/material';
import {
  Dashboard,
  Person,
  BarChart,
  TrendingUp,
  Settings,
  Help,
  Logout,
  AdminPanelSettings
} from '@mui/icons-material';

const drawerWidth = 280;

const Sidebar = ({ open, onClose, user, onLogout, onNavigate }) => {
  const menuItems = [
    { icon: <Dashboard />, text: 'Constituency Dashboard', view: 'dashboard' },
    { icon: <BarChart />, text: 'Candidate Dashboard', view: 'candidate' },
    { icon: <TrendingUp />, text: 'Party Dashboard', view: 'party' },
    { icon: <Person />, text: 'My Profile', view: 'profile' },
    { icon: <Settings />, text: 'Settings', view: 'settings' },
    { icon: <Help />, text: 'Help & Support', view: 'help' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('civiq_user');
    onLogout();
    onClose();
  };

  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          background: 'linear-gradient(180deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          border: 'none',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            CivIQ
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            Political Intelligence Platform
          </Typography>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

        {/* User Profile */}
        <Paper
          sx={{
            m: 2,
            p: 2,
            background: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <Avatar
            sx={{
              width: 60,
              height: 60,
              margin: '0 auto 12px',
              bgcolor: 'rgba(255,255,255,0.2)',
              fontSize: '1.5rem',
              fontWeight: 'bold'
            }}
          >
            <AdminPanelSettings />
          </Avatar>
          <Typography variant="subtitle1" fontWeight="600" gutterBottom>
            {user?.name || 'Administrator'}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {user?.role || 'System Admin'}
          </Typography>
          <Typography variant="caption" display="block" sx={{ opacity: 0.7, mt: 0.5 }}>
            {user?.email || 'admin@civiq.com'}
          </Typography>
        </Paper>

        {/* Navigation Menu */}
        <Box sx={{ flexGrow: 1, px: 1 }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  sx={{
                    borderRadius: 2,
                    mx: 1,
                    mb: 0.5,
                    color: 'white',
                    backgroundColor: false ? 'rgba(255,255,255,0.15)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                  onClick={() => {
                    if (item.view && typeof onNavigate === 'function') {
                      onNavigate(item.view);
                    }
                    onClose();
                  }}
                >
                  <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: 400,
                      fontSize: '0.95rem'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Logout Button */}
        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.3)',
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.5)',
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Sign Out
          </Button>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            CivIQ Dashboard v2.0
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
